import {
  adjustContentVisibility,
  Section,
  SectionParagraph as Subtitle,
  SectionTitle as Title,
} from '@/components/shared/Content';
import { useElementLerp } from '@/hooks/useElementLerp';
import { useSectionEventOnViewportEntrance } from '@/hooks/useSectionEventOnViewportEntrance';
import useSmoothScroll from '@/hooks/useSmoothScroll';
import { Box, Flex, font, useLottie } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import parse from 'html-react-parser';
import Image from 'next/image';
import React, { memo, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';
import styled from 'styled-components';
import { fastHelpImg, leftPhoneLottieData, rightPhoneLottieData, thereWhenNeededImg } from './assets/claims';

const ThereWhenNeeded = styled(Image).attrs({
  src: thereWhenNeededImg.src,
  alt: 'There when you need it',
})`
  position: absolute;
  position: absolute;
  left: 16px;
  transform: translateX(-100%);
  top: -17px;
  display: none;

  ${MEDIA_QUERIES.desktop`
      display: block;
    `}
`;

const FastHelp = styled(Image).attrs({
  src: fastHelpImg.src,
  alt: 'Fast help and claims resolution',
})`
  position: absolute;
  left: 100%;
  transform: translateX(-30px);
  top: -40px;
  display: none;

  ${MEDIA_QUERIES.desktop`
      display: block;
    `}
`;

const Explanation = styled(Box)`
  display: block;
  ${font('main', {
    fontSize: '1.6rem',
    lineHeight: '1.25',
  })}
  ${MEDIA_QUERIES.tablet`
    ${font('main', {
      fontSize: '2rem',
      lineHeight: '1.25',
    })}
  `}
  ${MEDIA_QUERIES.desktop`
    display: none
  `}
`;

const StyledSection = styled(Section)`
  ${adjustContentVisibility};
`;

interface ClaimsProps {
  t?: number;
  title?: string;
  subtitle?: string;
}

interface TitleSectionProps {
  title: string;
  subtitle: string;
}

const TitleSection = memo(
  ({ title, subtitle }: TitleSectionProps) => (
    <Flex gap="20px" flexDirection="column" width="100%">
      <Title tabIndex={0}>{parse(title)}</Title>
      <Box mx="auto" width="90%">
        <Subtitle tabIndex={0}>{parse(subtitle)}</Subtitle>
      </Box>
    </Flex>
  ),
  (prevProps, nextProps) => prevProps.title === nextProps.title && prevProps.subtitle === nextProps.subtitle,
);

TitleSection.displayName = 'TitleSection';

const Claims: React.FC<ClaimsProps> = ({
  t,
  title = '21st-century, award-winning claims service',
  subtitle = 'Our claims process is fast, seamless, and hassle-free. We’ll have your back whether you file a claim after\na crash, if your car’s been vandalized, or if you need a tow in the middle of nowhere.',
}) => {
  const { absoluteScroll } = useSmoothScroll();
  const [ref, newT] = useElementLerp(t ? t : absoluteScroll, {
    startPoint: 0.3,
  });
  const leftPhoneLottieRef = useRef<HTMLDivElement>(null);
  const rightPhoneLottieRef = useRef<HTMLDivElement>(null);

  useLottie(leftPhoneLottieRef, {
    animationData: leftPhoneLottieData,
    lottieConfig: {
      loop: true,
      renderer: 'svg',
    },
    scrollOptions: {
      pauseOnScroll: false,
      playTrigger: {
        threshold: 0,
        condition: 'contain',
        pauseIfConditionsUnmet: true,
      },
    },
  });

  useLottie(rightPhoneLottieRef, {
    animationData: rightPhoneLottieData,
    lottieConfig: {
      loop: true,
      renderer: 'svg',
    },
    scrollOptions: {
      pauseOnScroll: false,
      playTrigger: {
        threshold: 0,
        condition: 'contain',
        pauseIfConditionsUnmet: true,
      },
    },
  });

  const intersectionRef = useSectionEventOnViewportEntrance({
    sectionName: 'claims',
    page: 'car',
  });

  return (
    <StyledSection
      mb={0}
      pt={[45, 100]}
      pb={150}
      width={1}
      ref={mergeRefs([ref, intersectionRef])}
      aria-label="Lemonade car insurance app, faster everything"
      contain="paint"
    >
      <TitleSection title={title} subtitle={subtitle} />
      <Flex mt={40}>
        <Box
          height={[290, 500]}
          mr={[-15, -30, 10]}
          style={{ zIndex: 1, transform: `translateY(${newT * 50}px)` }}
          ref={leftPhoneLottieRef}
        >
          <ThereWhenNeeded width={129} height={111} />
          <Explanation mr={[10, 40]}>
            <b>There when</b>
            <br />
            <span>you need us</span>
          </Explanation>
        </Box>
        <Box
          height={[290, 500]}
          ml={[-15, -30, 10]}
          style={{ transform: `translateY(${(1 - newT) * 50}px)` }}
          ref={rightPhoneLottieRef}
        >
          <FastHelp width={165} height={111} />
          <Explanation ml={[10, 0]}>
            <b>Super fast</b>
            <br />
            <span>claim handling</span>
          </Explanation>
        </Box>
      </Flex>
    </StyledSection>
  );
};

export default React.memo(Claims);
